import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Ticket, TicketStatus } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { formatDate } from '../../lib/utils.ts';
import { StatusBadge, PriorityBadge, QueueBadge, LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';

const STATUS_TABS: { value: TicketStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress-l1', label: 'In Progress (L1)' },
  { value: 'in-progress-l2', label: 'In Progress (L2)' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

export default function Support() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<{ items: Ticket[]; total: number }>('/tickets');
      if (res.success && res.data) {
        setTickets(res.data.items);
      }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = statusFilter
    ? tickets.filter((t) => t.status === statusFilter)
    : tickets;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-dark">Customer Support</h1>
          <p className="text-gray-500 mt-1">Manage your support tickets</p>
        </div>
        <Link to="/support/new" className="btn-gradient px-5 py-2.5 rounded-xl text-sm font-semibold">
          New Ticket
        </Link>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.value
                ? 'bg-lime text-dark shadow-md'
                : 'bg-white text-gray-600 hover:bg-lime/20 border border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="💬"
          title="No tickets found"
          description={statusFilter ? 'No tickets match this filter' : 'You haven\'t created any support tickets'}
          action={
            !statusFilter ? (
              <Link to="/support/new" className="btn-gradient px-6 py-3 rounded-xl inline-block">
                Open a Ticket
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3 stagger-children">
          {filtered.map((ticket) => (
            <Link
              key={ticket.id}
              to={`/support/${ticket.id}`}
              className="block bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <StatusBadge status={ticket.status} />
                    <PriorityBadge priority={ticket.priority} />
                    <QueueBadge queue={ticket.queue} />
                    {ticket.orderId && (
                      <span className="text-xs text-gray-400">
                        Order #{ticket.orderId.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-dark truncate">{ticket.subject}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {ticket.messages.length} message{ticket.messages.length !== 1 && 's'} · Updated {formatDate(ticket.updatedAt)}
                  </p>
                </div>
                <svg className="w-5 h-5 text-gray-400 shrink-0 mt-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
